import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import WheelPage from '../components/WheelPage';
import AnimatedHeroWheel from '../components/AnimatedHeroWheel';
import { VARIANT_SLUGS } from '../App';
import {
  Zap, Globe, Shield, Wifi, WifiOff, Smartphone,
  ChevronRight, ChevronDown, Users, Gamepad2,
  Gift, UserCheck, Dices, HelpCircle
} from 'lucide-react';

// Variant cards with icons - slugs are fetched dynamically from VARIANT_SLUGS
const VARIANT_CARDS = [
  { key: 'yesNo', icon: '✅❌', color: '#10B981' },
  { key: 'namePicker', icon: '👤', color: '#3B82F6' },
  { key: 'teamGenerator', icon: '👥', color: '#8B5CF6' },
  { key: 'numberWheel', icon: '🔢', color: '#F59E0B' },
  { key: 'letterWheel', icon: '🔤', color: '#EC4899' },
  { key: 'colorWheel', icon: '🎨', color: '#06B6D4' },
  { key: 'foodWheel', icon: '🍕', color: '#EF4444' },
  { key: 'moviePicker', icon: '🎬', color: '#6366F1' },
  { key: 'gamePicker', icon: '🎮', color: '#14B8A6' },
  { key: 'giveawayWheel', icon: '🎁', color: '#F97316' },
  { key: 'truthOrDare', icon: '🎲', color: '#A855F7' },
  { key: 'questionWheel', icon: '❓', color: '#0EA5E9' },
  { key: 'casinoWheel', icon: '🎰', color: '#DC2626' },
  { key: 'datePicker', icon: '📅', color: '#059669' },
  { key: 'countryWheel', icon: '🌍', color: '#2563EB' },
  { key: 'drinkingGame', icon: '🍺', color: '#D97706' },
  { key: 'musicWheel', icon: '🎵', color: '#7C3AED' },
  { key: 'sportsWheel', icon: '⚽', color: '#16A34A' },
  { key: 'activityWheel', icon: '🎯', color: '#DB2777' }
];

// Helper to get localized slug for a variant
const getLocalizedSlug = (variantKey, lang) => {
  const langSlugs = VARIANT_SLUGS[lang] || VARIANT_SLUGS['en'];
  return langSlugs[variantKey] || VARIANT_SLUGS['en'][variantKey];
};

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
    <button 
      className={`faq-question ${isOpen ? 'open' : ''}`}
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span itemProp="name">{question}</span>
      <ChevronDown size={20} className={`faq-icon ${isOpen ? 'rotated' : ''}`} />
    </button>
    {isOpen && (
      <div 
        className="faq-answer" 
        itemScope 
        itemProp="acceptedAnswer" 
        itemType="https://schema.org/Answer"
      >
        <p itemProp="text">{answer}</p>
      </div>
    )}
  </div>
);

const HomePage = () => {
  const { t, language, isRTL } = useLanguage();
  const [openFAQ, setOpenFAQ] = React.useState(0);
  
  // Get translated FAQ items
  const getFaqItems = () => {
    try {
      const translated = t('faq.items', { returnObjects: true });
      if (Array.isArray(translated) && translated.length > 0) {
        return translated;
      }
    } catch (e) {
      // Fallback to empty array if translation fails
    }
    return [];
  };
  const faqItems = getFaqItems();
  
  // Get use cases translations
  const useCases = [
    { 
      icon: <UserCheck size={32} />, 
      title: t('homepage.useCases.teachers.title'), 
      description: t('homepage.useCases.teachers.description') 
    },
    { 
      icon: <Gift size={32} />, 
      title: t('homepage.useCases.streamers.title'), 
      description: t('homepage.useCases.streamers.description') 
    },
    { 
      icon: <Users size={32} />, 
      title: t('homepage.useCases.teams.title'), 
      description: t('homepage.useCases.teams.description') 
    },
    { 
      icon: <Gamepad2 size={32} />, 
      title: t('homepage.useCases.partyGames.title'), 
      description: t('homepage.useCases.partyGames.description') 
    },
  ];

  return (
    <>
      {/* Hero Section with Animated Wheel */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              {t('hero.title')}
              <span className="hero-highlight">{t('hero.highlight')}</span>
            </h1>
            <p className="hero-subtitle">
              {t('hero.subtitle')}
            </p>
            <div className="hero-features">
              <span className="hero-badge"><Shield size={16} /> {t('hero.badge1')}</span>
              <span className="hero-badge"><Wifi size={16} /> {t('hero.badge2')}</span>
              <span className="hero-badge"><Globe size={16} /> {t('hero.badge3')}</span>
            </div>
            <div className="hero-cta">
              <Link to={language === 'en' ? `/${getLocalizedSlug('namePicker', language)}` : `/${language}/${getLocalizedSlug('namePicker', language)}`} className="btn-hero-primary">
                {t('hero.cta1')} <ChevronRight size={18} />
              </Link>
              <a href="#all-variants" className="btn-hero-secondary">
                {t('hero.cta2')}
              </a>
            </div>
          </div>
          <div className="hero-wheel-container">
            <AnimatedHeroWheel />
          </div>
        </div>
      </section>
      
      {/* Main Interactive Wheel Section */}
      <section className="interactive-wheel-section">
        <div className="section-container">
          <h2 className="section-title">{t('hero.tryNow')}</h2>
          <p className="section-subtitle">{t('hero.tryNowSubtitle')}</p>
        </div>
      </section>
      <WheelPage variant="main" />
      
      {/* Use Cases Section */}
      <section className="use-cases-section" data-testid="use-cases-section">
        <div className="section-container">
          <h2 className="section-title">{t('homepage.useCasesTitle')}</h2>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card" data-testid={`use-case-${index}`}>
                <div className="use-case-icon">{useCase.icon}</div>
                <h3>{useCase.title}</h3>
                <p>{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Variants Section */}
      <section className="all-variants-section" id="all-variants" data-testid="all-variants-section">
        <div className="section-container">
          <h2 className="section-title">{t('homepage.variantsTitle')}</h2>
          <p className="section-subtitle">{t('homepage.variantsSubtitle')}</p>
          
          <div className="variants-grid-full">
            {VARIANT_CARDS.map(({ key, icon, color }) => {
              const slug = getLocalizedSlug(key, language);
              const path = language === 'en' ? `/${slug}` : `/${language}/${slug}`;
              return (
                <Link 
                  key={key}
                  to={path}
                  className="variant-card-full"
                  style={{ '--accent-color': color }}
                  data-testid={`variant-card-${key}`}
                >
                  <span className="variant-icon-large" aria-hidden="true">{icon}</span>
                  <div className="variant-info">
                    <h3>{t(`variants.${key}.title`)}</h3>
                    <p>{t(`variants.${key}.description`)}</p>
                  </div>
                  <ChevronRight size={20} className="variant-arrow" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* How It Works / Randomness Trust Section — the single canonical copy.
          Every variant page links here instead of repeating this explanation
          (it was previously duplicated verbatim on all 19 variant pages). */}
      <section className="seo-section how-it-works" id="how-it-works" data-testid="how-it-works-section">
        <div className="section-container">
          <h2>
            <Shield size={24} />
            {t('seo.algorithm.title')}
          </h2>
          <p className="algo-intro" dangerouslySetInnerHTML={{ __html: t('seo.algorithm.intro') }} />
          <div className="algorithm-explanation">
            <div className="algo-feature">
              <div className="algo-icon">🔐</div>
              <h3>{t('seo.algorithm.crypto.title')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('seo.algorithm.crypto.desc') }} />
            </div>
            <div className="algo-feature">
              <div className="algo-icon">⚖️</div>
              <h3>{t('seo.algorithm.fairness.title')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('seo.algorithm.fairness.desc') }} />
            </div>
            <div className="algo-feature">
              <div className="algo-icon">🛡️</div>
              <h3>{t('seo.algorithm.tamper.title')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('seo.algorithm.tamper.desc') }} />
            </div>
          </div>
          <div className="algo-trust">
            <p dangerouslySetInnerHTML={{ __html: t('seo.algorithm.trust') }} />
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="comparison-section" data-testid="comparison-section">
        <div className="section-container">
          <h2 className="section-title">{t('homepage.comparisonTitle')}</h2>
          
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>{t('homepage.comparison.feature')}</th>
                  <th>{t('homepage.comparison.others')}</th>
                  <th className="highlight">wheel.expert</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('homepage.comparison.price')}</td>
                  <td>{t('homepage.comparison.priceOthers')}</td>
                  <td className="highlight">✅ {t('homepage.comparison.priceSelf')}</td>
                </tr>
                <tr>
                  <td>{t('homepage.comparison.account')}</td>
                  <td>{t('homepage.comparison.accountOthers')}</td>
                  <td className="highlight">✅ {t('homepage.comparison.accountSelf')}</td>
                </tr>
                <tr>
                  <td>{t('homepage.comparison.languages')}</td>
                  <td>{t('homepage.comparison.languagesOthers')}</td>
                  <td className="highlight">✅ {t('homepage.comparison.languagesSelf')}</td>
                </tr>
                <tr>
                  <td>{t('homepage.comparison.variants')}</td>
                  <td>{t('homepage.comparison.variantsOthers')}</td>
                  <td className="highlight">✅ {t('homepage.comparison.variantsSelf')}</td>
                </tr>
                <tr>
                  <td>{t('homepage.comparison.offline')}</td>
                  <td>{t('homepage.comparison.offlineOthers')}</td>
                  <td className="highlight">✅ {t('homepage.comparison.offlineSelf')}</td>
                </tr>
                <tr>
                  <td>{t('homepage.comparison.accessibility')}</td>
                  <td>{t('homepage.comparison.accessibilityOthers')}</td>
                  <td className="highlight">✅ {t('homepage.comparison.accessibilitySelf')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section with Schema.org */}
      <section 
        className="faq-section" 
        id="faq"
        itemScope 
        itemType="https://schema.org/FAQPage"
        data-testid="faq-section"
      >
        <div className="section-container">
          <h2 className="section-title">
            <HelpCircle size={28} />
            {t('faq.title')}
          </h2>
          
          <div className="faq-list">
            {faqItems.length > 0 ? faqItems.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.q || faq.question}
                answer={faq.a || faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            )) : null}
          </div>
        </div>
      </section>
      
      {/* Trust Indicators */}
      <section className="trust-section" data-testid="trust-section">
        <div className="section-container">
          <div className="trust-badges">
            <div className="trust-badge">
              <Globe size={24} />
              <span>{t('hero.badge3')}</span>
            </div>
            <div className="trust-badge">
              <Shield size={24} />
              <span>{t('features.privacy.title')}</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
